/**
 * Comparing two model inventories from metadata alone — the free tier of
 * compare mode.
 *
 * **Zero payload bytes, zero requests.** Both safetensors headers are already in
 * hand once each repo has loaded, so this entire view is arithmetic over data we
 * have. It costs nothing and can render the instant the second repo resolves,
 * which is what lets it be the landing view of compare mode rather than
 * something a visitor pays for by waiting.
 *
 * **The reason this is not a set intersection.** When B is an MLX-style affine
 * quantization of A, one logical tensor `…q_proj.weight` is stored in B as a
 * *triple*: a packed `…q_proj.weight` (U32, and a different shape), plus
 * `…q_proj.scales` and `…q_proj.biases` which have no counterpart in A at all. A
 * name-set diff therefore reports every quantized tensor as one shape change and
 * two additions, so a 4-bit quantization of a 24-layer model reads as several
 * hundred unrelated rows — burying the single fact the reader came for ("B is
 * the same model at 4 bits") under noise it has to be talked out of. Both sides
 * are folded into logical tensors first, exactly as `anatomy.ts` does and for
 * exactly the same reason.
 *
 * **The other side of the diff is the anchor that resolves the scheme.** A
 * quantized triple's shapes constrain only the *product* `bits × groupSize` (see
 * `affineShapeCandidates` — `[64,32]` packed against `[64,4]` scales is equally
 * consistent with 8-bit/group 32, 4-bit/group 64 and 2-bit/group 128), so
 * `findAffineGroups` with no `config.json` leaves most real triples unresolved.
 * But in a *comparison* the counterpart tensor in A states the logical width
 * outright: it is a plain matrix and its last dimension is `in_features`. That
 * is the same kind of evidence `readAnchor` takes from `hidden_size` — a
 * measured dimension, not a claim — just sourced from the other repo instead of
 * from a config. Selecting the one candidate whose `cols` equals A's width
 * resolves the scheme without fetching anything, and the selection is
 * unambiguous because `cols = groupSize × scaleCols` is injective in the group
 * size. When *no* candidate matches A's width, that failure is itself the
 * finding: the two tensors cannot reconstruct to the same shape, so the row is
 * a shape change rather than a quantization.
 *
 * **What is folded and what is not.** Parameter counts are *logical*, so a 4-bit
 * B does not appear to have an eighth of A's parameters — that would be an
 * artefact of the packing, not a fact about the model. Byte counts are the raw
 * bytes on disk and are never folded, because a genuine 3.9× byte reduction is
 * the single most interesting number in the whole comparison and normalizing it
 * away would be deleting the answer.
 *
 * Pure: two inventories in, one value out. No DOM, no network, no clock.
 */

import type { Inventory, InventoryTensor } from "../safetensors/inventory.js";
import {
  type LogicalTensor,
  classifyAll,
  layerGroupOf,
  toLogicalTensors,
} from "./anatomy.js";
import {
  type AffineGroup,
  type AffineShapeCandidate,
  findAffineGroups,
} from "./mlx.js";

export type TensorDiffStatus =
  | "identical" // same dtype, same shape
  | "dtype-changed" // same logical shape, different dtype (f32 -> bf16)
  | "quantized" // B stores this as a quantized triple; see below
  | "dequantized" // the reverse
  | "shape-changed" // logical shapes differ — not comparable
  | "only-a"
  | "only-b";

export interface TensorDiffEntry {
  readonly name: string;
  readonly a: InventoryTensor | undefined;
  readonly b: InventoryTensor | undefined;
  readonly status: TensorDiffStatus;
  /**
   * What B calls this tensor, when that is not what A calls it.
   *
   * `undefined` for the ordinary case where both sides agree, so a table can
   * show the second name only where there is a second name to show. The rekey
   * pairs by what a tensor *is* rather than by what it is called, which means a
   * row can now legitimately carry two names — and dropping one of them would
   * be the page picking a publisher.
   */
  readonly bName?: string | undefined;
  /** True when both sides reconstruct to the same logical [rows, cols]. */
  readonly comparable: boolean;
  /** One short human sentence for this row, e.g. "F16 -> 4-bit affine". */
  readonly note: string;
}

export interface SideTotals {
  /**
   * *Logical* tensors, not stored ones — a quantized triple counts once. Without
   * this, quantizing a model would look like it tripled the tensor count.
   */
  readonly tensors: number;
  /**
   * Logical parameters. A quantized triple contributes `rows × cols` of the
   * matrix it reconstructs to, never the packed element count.
   */
  readonly params: number;
  /** Raw bytes on disk, unfolded — the number the comparison exists to show. */
  readonly bytes: number;
}

export interface InventoryDiff {
  readonly entries: readonly TensorDiffEntry[];
  /**
   * The seven status counters below **partition `entries`** — one per member of
   * `TensorDiffStatus`, and they sum to `entries.length`. Worth stating because
   * an earlier revision omitted `dequantized`, and both consumers independently
   * tripped over the same consequence: any "do these repos overlap at all"
   * question computed by summing the counters scored a repo pair where B is a
   * dequantization of A at zero matches, sending the most interesting lineage
   * case straight down the "unrelated" path. A partial partition invites
   * exactly that arithmetic; a complete one makes it correct.
   */
  /** Same shape, same dtype, same storage on both sides. */
  readonly identical: number;
  /** Same logical shape, different dtype — including one scheme to another. */
  readonly dtypeChanged: number;
  /**
   * Rows with status `"quantized"` — B packs what A stored as plain floats.
   *
   * One direction only; the reverse is `dequantized`. Kept as two counters
   * rather than one signed "requantized" tally because the two directions are
   * different findings — B being a quantization of A is the ordinary case, and
   * B being a *de*quantization of A means someone published upcast weights,
   * which is worth noticing rather than averaging away.
   */
  readonly quantized: number;
  /** Rows with status `"dequantized"` — B stores as plain floats what A packed. */
  readonly dequantized: number;
  readonly shapeChanged: number;
  readonly onlyA: number;
  readonly onlyB: number;
  /** Entries a per-tensor comparison could actually run on. */
  readonly comparable: number;
  readonly a: SideTotals;
  readonly b: SideTotals;
  /** Per-dtype byte totals on both sides, for "where did the bytes go". */
  readonly dtypeShift: readonly {
    readonly dtype: string;
    readonly aBytes: number;
    readonly bBytes: number;
  }[];
  /** One plain sentence naming what B is, relative to A. */
  readonly verdict: string;
  /**
   * Whether the two sides look like the same stack spelled differently.
   *
   * Exposed rather than only folded into `verdict`, because a **second**
   * consumer needs it and was contradicting the verdict without it:
   * `compare-view.ts` shorts out to "these don't look related — nothing here
   * suggests one of these came from the other" whenever name overlap is thin,
   * which is exactly what a renamed stack looks like. On the extraction case the
   * page printed both sentences, one under the other, beneath a grid that had
   * just matched 385 of A's 385 cells to a stack of B.
   *
   * Zero name overlap has two readings that deserve opposite messages, and the
   * arithmetic cannot tell them apart — so the detector's answer has to travel
   * rather than be re-derived by whoever needs it next.
   */
  readonly namingDiffers: boolean;
}

/**
 * One side's view of a logical tensor, carrying the parts `anatomy.ts` folded
 * plus the affine group `mlx.ts` recognized.
 *
 * `shape`, `scheme` and `params` are mutable because cross-side anchoring is a
 * second pass: a triple that no single side could resolve becomes resolvable the
 * moment its counterpart is known. Nothing outside this module ever sees the
 * mutable form — `diffInventories` builds these, resolves them, and reads them.
 */
interface SideTensor {
  readonly name: string;
  readonly parts: readonly InventoryTensor[];
  /** The payload part — the packed weight for a triple, the tensor otherwise. */
  readonly main: InventoryTensor;
  readonly bytes: number;
  readonly affine: AffineGroup | undefined;
  /**
   * The shape this reconstructs to, or `undefined` for an affine triple whose
   * (bits, group size) nothing has pinned down. Rank is preserved rather than
   * collapsed to two dims, so a stacked MoE expert bank stays distinguishable
   * from a plain matrix with the same element count.
   */
  shape: readonly number[] | undefined;
  scheme: AffineShapeCandidate | undefined;
  params: number;
}

/**
 * Fold one inventory into logical tensors, keyed by logical name.
 *
 * Two scans of the same tensor list: `toLogicalTensors` for the grouping (reused
 * rather than reimplemented — a second copy of the suffix rules is how the two
 * views start disagreeing about what a tensor is), and `findAffineGroups` for
 * the affine geometry, which `LogicalTensor` deliberately does not expose. Both
 * are metadata-only passes over a few hundred entries, so the duplication costs
 * nothing measurable.
 *
 * Neither a declared quantization nor architecture anchors are passed: an
 * `Inventory` carries headers, not `config.json`. Most real triples therefore
 * arrive unresolved, and `anchorFromPeer` is what resolves them.
 */
function readSide(tensors: readonly InventoryTensor[]): Map<string, SideTensor> {
  const groups = new Map(findAffineGroups(tensors).groups.map((g) => [g.prefix, g]));

  const side = new Map<string, SideTensor>();
  for (const logical of toLogicalTensors(tensors)) {
    const main = mainPart(logical);
    if (main === undefined) continue;

    const affine = groups.get(logical.name);
    const scheme = affine?.resolved;
    side.set(logical.name, {
      name: logical.name,
      parts: logical.parts,
      main,
      bytes: logical.bytes,
      affine,
      shape:
        affine === undefined
          ? main.shape
          : scheme === undefined
            ? undefined
            : [...main.shape.slice(0, -1), scheme.cols],
      scheme,
      // An unresolved triple reports zero rather than a guess, matching
      // `toLogicalTensors`: being wrong here scales the side's headline
      // parameter count by exactly the factor the bits/group split is wrong by.
      params:
        affine === undefined
          ? logical.params
          : scheme === undefined
            ? 0
            : affine.rows * scheme.cols,
    });
  }
  return side;
}

/**
 * The payload part of a logical group — same selection `anatomy.ts` makes, and
 * it has to be the same one or the two views would report different dtypes and
 * shapes for the same tensor.
 */
function mainPart(logical: LogicalTensor): InventoryTensor | undefined {
  return (
    logical.parts.find((p) => p.name === `${logical.name}.weight`) ??
    logical.parts.find((p) => p.name === logical.name) ??
    logical.parts[0]
  );
}

/**
 * Resolve an unpinned affine triple against its counterpart in the other repo.
 *
 * The counterpart is an anchor in the same sense `readAnchor` uses the config's
 * `hidden_size`: a logical width measured off a real tensor, not asserted. If A
 * stores `…q_proj.weight` as `[64, 256]` and B stores a triple whose candidates
 * are 8-bit/group 32 (cols 128), 4-bit/group 64 (cols 256) and 2-bit/group 128
 * (cols 512), then only the middle one reconstructs to something A could be
 * compared against — and `cols = groupSize × scaleCols` is injective in the
 * group size, so "only one candidate matches" is a property of the arithmetic
 * rather than a tie-break.
 *
 * Every check is a reason to decline rather than to force:
 *
 *  - a side that already resolved is never overwritten, so evidence from the
 *    shapes themselves outranks evidence borrowed from the other repo;
 *  - the ranks and all leading dims must agree, so a 3-D expert bank is not
 *    anchored against a 2-D matrix that happens to end in the same width;
 *  - no matching candidate means no anchor. That is not a failure to be papered
 *    over — it is the finding that these two tensors cannot reconstruct to the
 *    same shape, and the caller reads it as a shape change.
 */
function anchorFromPeer(target: SideTensor, peer: SideTensor): void {
  const affine = target.affine;
  if (affine === undefined || target.scheme !== undefined) return;

  const peerShape = peer.shape;
  if (peerShape === undefined) return;

  const packed = target.main.shape;
  if (packed.length !== peerShape.length) return;
  if (!sameDims(packed.slice(0, -1), peerShape.slice(0, -1))) return;

  const cols = peerShape[peerShape.length - 1];
  if (cols === undefined) return;

  const candidate = affine.candidates.find((c) => c.cols === cols);
  if (candidate === undefined) return;

  target.scheme = candidate;
  target.shape = [...packed.slice(0, -1), candidate.cols];
  target.params = affine.rows * candidate.cols;
}

/**
 * Row order, most-to-least urgent.
 *
 * A quantization diff is hundreds of rows of which a handful matter, and a table
 * sorted by tensor name puts `model.layers.0.…` first purely because zero sorts
 * before one. So rows are ranked by what a reader has to *act* on:
 *
 *  1. `shape-changed` — the models do not line up here, and nothing downstream
 *     can compare these two tensors. Always the first thing to know.
 *  2. `only-a` — something present in the base is *gone*. Ranked above `only-b`
 *     because a disappearance is the more consequential surprise: an extra
 *     tensor in B is usually a scheme's bookkeeping, a missing one is a model
 *     that may not load.
 *  3. `only-b`.
 *  4. `quantized` / `dequantized` — the expected difference when comparing a
 *     quant against its base, so it is signal but not alarm.
 *  5. `dtype-changed`.
 *  6. `identical` — the hundreds of rows that say nothing, last.
 *
 * Within a rank, largest tensor first (by the heavier side's bytes), because
 * bytes are what the reader came to compare. Ties break on name so the order is
 * total and deterministic — which it must be, since every layer's `q_proj` is
 * byte-for-byte the same size as every other layer's.
 */
const STATUS_ORDER: Readonly<Record<TensorDiffStatus, number>> = {
  "shape-changed": 0,
  "only-a": 1,
  "only-b": 2,
  quantized: 3,
  dequantized: 4,
  "dtype-changed": 5,
  identical: 6,
};

/**
 * Below this much name overlap, two inventories are reported as unrelated rather
 * than diffed row by row.
 *
 * Measured against the *smaller* side, so a 70B compared with a 7B of the same
 * architecture — where the small model's names are a subset of the large one's —
 * still counts as related and gets a real diff. Everything the tool is actually
 * for sits far above this line: a finetune shares 100% of names, a quantization
 * shares 100% after folding, a sibling size shares all of the smaller side. Two
 * unrelated architectures share almost nothing, and pasting two unrelated repos
 * is a mistake a visitor will make, so the honest response is to say so in one
 * sentence instead of rendering six hundred rows that all mean "these are
 * different models".
 */
export const UNRELATED_OVERLAP = 0.5;

/**
 * The stack root a side hangs everything off — what to strip so two spellings of
 * one model line up.
 *
 * The most common root among the side's layer tensors, because that is the one
 * the stack itself uses and a stray tensor under another prefix should not move
 * it. `model.` for a text-only extraction, `model.language_model.` for the tower
 * it came from.
 *
 * Non-layer tensors keep their own prefix, which is exactly what makes stripping
 * the *stack's* root work on them too: `model.embed_tokens.weight` and
 * `model.language_model.embed_tokens.weight` both reduce to
 * `embed_tokens.weight`, so the embedding pairs with the embedding without any
 * rule about embeddings.
 */
function dominantRoot(tensors: readonly InventoryTensor[]): string {
  const counts = new Map<string, number>();
  for (const tensor of tensors) {
    const { layer, stackRoot } = layerGroupOf(tensor.name);
    if (layer === undefined || stackRoot === "") continue;
    counts.set(stackRoot, (counts.get(stackRoot) ?? 0) + 1);
  }
  let best = "";
  let seen = 0;
  for (const [root, count] of counts) {
    if (count > seen) {
      best = root;
      seen = count;
    }
  }
  return best;
}

/**
 * An expert's index inside its layer — a **true** sub-key, not an ordinal.
 *
 * An MoE layer holds hundreds of `mlp.gate` tensors that differ only by expert
 * number, and that number means the same thing on both sides. It is the one
 * position-looking integer in a tensor name that may be matched on, because it
 * is part of the model's own addressing rather than of the order a file happens
 * to list things in.
 */
const EXPERT_INDEX = /(?:^|\.)experts?\.(\d+)(?:\.|$)/;

/**
 * Pair two sides' logical tensors, finest identity first.
 *
 * Two tiers, and the order is the argument:
 *
 *  1. **The name with its stack root removed.** Exact, and it is what the
 *     extraction case needs: `model.layers.0.input_layernorm` and
 *     `model.language_model.layers.0.input_layernorm` are the same tensor
 *     written by two publishers, and stripping the root says so without any
 *     classification at all. It is also *finer* than a cell — a layer has four
 *     norms and this keeps them apart, where `(layer, "norm")` would not.
 *  2. **`(layer, role, expert)` for whatever tier 1 left over**, which is the
 *     cross-convention case: GGUF's `blk.0.attn_q` shares no leaf name with
 *     `layers.0.self_attn.q_proj`, so only what the tensors *do* can pair them.
 *     Paired **only when exactly one entry on each side lands in that key** —
 *     anything else is marked unmatched rather than aligned by position
 *     (reviewer, §142). Ordinal alignment inside a cell is a guess that renders
 *     as a fact.
 *
 * Never invents a pairing it cannot justify: a tensor that reaches the end
 * unpaired is reported one-sided, which is the honest answer and the one the
 * caller already knows how to draw.
 */
interface Pairing {
  readonly key: string;
  readonly a: SideTensor | undefined;
  readonly b: SideTensor | undefined;
}

function pairSides(
  a: Inventory,
  aSide: ReadonlyMap<string, SideTensor>,
  b: Inventory,
  bSide: ReadonlyMap<string, SideTensor>,
  sameStack: boolean,
): Pairing[] {
  const aRoot = dominantRoot(a.tensors);
  const bRoot = dominantRoot(b.tensors);
  const strip = (name: string, root: string): string =>
    root !== "" && name.startsWith(root) ? name.slice(root.length) : name;

  const pairs: Pairing[] = [];
  const aLeft = new Map(aSide);
  const bLeft = new Map(bSide);

  // Tier 1: root-stripped names, which for a same-convention pair is every row.
  const bByStripped = new Map<string, SideTensor[]>();
  for (const tensor of bSide.values()) {
    const key = strip(tensor.name, bRoot);
    bByStripped.set(key, [...(bByStripped.get(key) ?? []), tensor]);
  }
  for (const tensor of aSide.values()) {
    const key = strip(tensor.name, aRoot);
    const candidates = bByStripped.get(key);
    // Exactly one, or it is not an identity. Two tensors reducing to one name
    // means the root guess was wrong for at least one of them.
    if (candidates?.length !== 1) continue;
    const match = candidates[0];
    if (match === undefined || !bLeft.has(match.name)) continue;
    pairs.push({ key, a: tensor, b: match });
    aLeft.delete(tensor.name);
    bLeft.delete(match.name);
  }

  /*
   * Tier 2: what the tensors do, for whatever the names could not reach — and
   * **only when the two sides are already known to be one stack under two
   * spellings**.
   *
   * The gate is not caution, it is the difference between a rekey and a
   * fabrication. Every transformer has an `attn.q` at layer 0, so pairing on
   * `(layer, role)` alone matches `openai-community/gpt2` against
   * `Qwen/Qwen3-8B` — six rows of "shape changed" where the truth is "these are
   * different models". That verdict then disappears, because a shape-changed row
   * counts as *matched* and the unrelated short-circuit stops firing: two
   * unrelated repos would render a full table instead of one honest sentence.
   *
   * This is #65's over-firing in a new place, and it takes the same answer —
   * role sets are what every transformer shares, so the licence to pair on them
   * has to come from somewhere else. `sameModelDifferentNames` is that evidence,
   * and it is the detector already measured against real repo pairs.
   */
  if (sameStack && aLeft.size > 0 && bLeft.size > 0) {
    const cellsOf = (
      inv: Inventory,
      left: ReadonlyMap<string, SideTensor>,
    ): Map<string, SideTensor[]> => {
      const roles = classifyAll(inv.tensors, undefined, undefined);
      const out = new Map<string, SideTensor[]>();
      for (const tensor of left.values()) {
        const role = roles.get(tensor.main.name)?.role;
        if (role === undefined) continue;
        const { layer } = layerGroupOf(tensor.main.name);
        const expert = EXPERT_INDEX.exec(tensor.main.name)?.[1] ?? "-";
        const key = `${layer ?? "-"}/${role}/${expert}`;
        out.set(key, [...(out.get(key) ?? []), tensor]);
      }
      return out;
    };
    const aCells = cellsOf(a, aLeft);
    const bCells = cellsOf(b, bLeft);
    for (const [key, mine] of aCells) {
      const theirs = bCells.get(key);
      /*
       * One on each side, or nothing. Four norms facing four norms is a cell
       * this tier cannot resolve — their order in the file is not evidence — so
       * they stay one-sided and say so.
       */
      if (mine.length !== 1 || theirs?.length !== 1) continue;
      const mineOne = mine[0];
      const theirsOne = theirs[0];
      if (mineOne === undefined || theirsOne === undefined) continue;
      pairs.push({ key, a: mineOne, b: theirsOne });
      aLeft.delete(mineOne.name);
      bLeft.delete(theirsOne.name);
    }
  }

  for (const tensor of aLeft.values()) {
    pairs.push({ key: tensor.name, a: tensor, b: undefined });
  }
  for (const tensor of bLeft.values()) {
    pairs.push({ key: tensor.name, a: undefined, b: tensor });
  }
  return pairs;
}

export function diffInventories(a: Inventory, b: Inventory): InventoryDiff {
  const aSide = readSide(a.tensors);
  const bSide = readSide(b.tensors);

  /*
   * Pairing first, then anchoring — and that order changed with the rekey.
   *
   * Anchoring used to walk `aSide.get(name)`, so a quantized triple could only
   * borrow its counterpart's width when the two repos already agreed on the
   * name. On a renamed pair nothing anchored, every triple stayed unresolved,
   * and each one was then reported as a shape change: the diff said the models
   * did not line up because it had not worked out which tensors were which.
   */
  const naming = sameModelDifferentNames(a, b);
  const pairs = pairSides(a, aSide, b, bSide, naming);
  for (const pair of pairs) {
    if (pair.a === undefined || pair.b === undefined) continue;
    anchorFromPeer(pair.b, pair.a);
    anchorFromPeer(pair.a, pair.b);
  }

  const rows: Array<{
    readonly entry: TensorDiffEntry;
    readonly rank: number;
    readonly bytes: number;
  }> = [];
  const quantBits: string[] = [];
  const dtypeMoves: string[] = [];

  for (const pair of pairs) {
    const aTensor = pair.a;
    const bTensor = pair.b;
    /*
     * A's name leads when there is one, because A is the grid and the subject.
     * When the two differ, B's is carried alongside rather than dropped: a
     * reader looking at a renamed pair needs to know *what it is called over
     * there*, and a row that quietly shows one of two names is the page
     * choosing which publisher to believe.
     */
    const name = aTensor?.name ?? bTensor?.name ?? pair.key;

    let entry: TensorDiffEntry;
    if (aTensor !== undefined && bTensor !== undefined) {
      const verdict = classifyPair(aTensor, bTensor);
      entry = {
        name,
        bName: bTensor.name === name ? undefined : bTensor.name,
        a: aTensor.main,
        b: bTensor.main,
        status: verdict.status,
        comparable: verdict.comparable,
        note: verdict.note,
      };
      if (verdict.status === "quantized") {
        quantBits.push(bTensor.scheme ? `${bTensor.scheme.bits}-bit` : "");
      }
      if (verdict.status === "dtype-changed") {
        dtypeMoves.push(`${aTensor.main.dtype} to ${bTensor.main.dtype}`);
      }
    } else if (aTensor !== undefined) {
      entry = {
        name,
        a: aTensor.main,
        b: undefined,
        status: "only-a",
        comparable: false,
        note: `only in A — ${describe(aTensor)}`,
      };
    } else if (bTensor !== undefined) {
      entry = {
        name,
        a: undefined,
        b: bTensor.main,
        status: "only-b",
        comparable: false,
        note: `only in B — ${describe(bTensor)}`,
      };
    } else {
      continue;
    }

    rows.push({
      entry,
      rank: STATUS_ORDER[entry.status],
      bytes: Math.max(aTensor?.bytes ?? 0, bTensor?.bytes ?? 0),
    });
  }

  rows.sort(
    (x, y) =>
      x.rank - y.rank ||
      y.bytes - x.bytes ||
      (x.entry.name < y.entry.name ? -1 : x.entry.name > y.entry.name ? 1 : 0),
  );
  const entries = rows.map((r) => r.entry);

  const count = (status: TensorDiffStatus): number =>
    entries.reduce((n, e) => n + (e.status === status ? 1 : 0), 0);

  const identical = count("identical");
  const dtypeChanged = count("dtype-changed");
  const quantized = count("quantized");
  const dequantized = count("dequantized");
  const shapeChanged = count("shape-changed");
  const onlyA = count("only-a");
  const onlyB = count("only-b");
  const comparable = entries.reduce((n, e) => n + (e.comparable ? 1 : 0), 0);

  const aTotals = sideTotals(a, aSide);
  const bTotals = sideTotals(b, bSide);
  // `naming` is computed above, before pairing, because it now *licenses* the
  // cell-based tier as well as wording the verdict — three consumers of one
  // finding, and computing it more than once is how they come to disagree.

  return {
    entries,
    identical,
    dtypeChanged,
    quantized,
    dequantized,
    shapeChanged,
    onlyA,
    onlyB,
    comparable,
    a: aTotals,
    b: bTotals,
    dtypeShift: dtypeShift(a, b),
    namingDiffers: naming,
    verdict: buildVerdict({
      total: entries.length,
      identical,
      dtypeChanged,
      quantized,
      dequantized,
      shapeChanged,
      onlyA,
      onlyB,
      comparable,
      a: aTotals,
      b: bTotals,
      quantBits,
      dtypeMoves,
      namingDiffers: naming,
    }),
  };
}

/**
 * Do these two name the same model differently?
 *
 * The question behind one sentence: when no tensor name matches, is that
 * because the models are unrelated, or because two publishers spell the same
 * architecture differently? Both readings share the arithmetic — zero overlap —
 * and they deserve opposite messages.
 *
 * ## Two wrong answers before this one
 *
 * First it tested for `blk.N.`, llama.cpp's layer prefix. That caught GGUF
 * against safetensors and nothing else, and missed
 * `mlx-community/Qwen3.6-27B-OptiQ-4bit` against `Qwen/Qwen3.6-27B` — both
 * safetensors, the prefix merely reordered (`model.language_model.…` against
 * `language_model.model.…`). Enumerating known conventions cannot keep up with
 * the ones publishers invent.
 *
 * Then it compared **role sets**, which over-fired in the other direction: every
 * transformer has an embedding, attention projections, an MLP and norms, so
 * `openai-community/gpt2` against `Qwen/Qwen3-8B` — genuinely different models —
 * claimed a naming difference. True, and useless: it stopped saying the thing a
 * reader needs, which is that these are different models.
 *
 * ## What actually separates the two
 *
 * Not whether the roles *exist* on both sides, but whether they line up **at the
 * same layer indices with the same widths**. A quantization and its parent have
 * the same stack depth and the same tensor rows under different names; two
 * different models do not — gpt2 is 12 layers of 768, Qwen3-8B is 36 of 4096.
 *
 * Rows rather than the full shape, and that is the load-bearing choice. An
 * affine quantization packs along the **last** axis, so `[4096, 4096]` becomes
 * `[4096, 512]` while the row count survives — and a triple whose bits/group
 * split could not be resolved reports no logical shape at all, which is
 * precisely the quant-against-parent case this exists to recognise. Keying on
 * rows is the part of the shape that packing cannot move.
 *
 * Still only a detector. It decides one sentence's wording and changes no
 * matching: pairing the two sides by classification is a rekey, and its own
 * milestone.
 */
function sameModelDifferentNames(a: Inventory, b: Inventory): boolean {
  const aCells = cellSignature(a.tensors);
  const bCells = cellSignature(b.tensors);
  if (aCells.size === 0 || bCells.size === 0) return false;
  /*
   * A majority of the smaller side. Two models that merely resemble each other
   * — same family, different size — will match a few cells and must still get
   * the harsher verdict; only a side that is substantially *the same stack*
   * under other names clears this.
   */
  return signatureOverlap(aCells, bCells) > 0.5;
}

/**
 * The name-independent identity of a set of tensors: which `(layer, role,
 * rows)` cells it occupies.
 *
 * **Exported because two questions need the same answer.** The verdict above
 * asks "are these the same stack under other names"; `analysis/tower-match.ts`
 * asks "which of B's stacks is the one A was extracted from". Both are the
 * question *what does this stack look like when its names cannot be trusted*,
 * and two implementations of it would be one edit from disagreeing — the same
 * argument that keeps `readSide` shared between the diff and the totals.
 *
 * Takes tensors rather than an `Inventory` for one reason: **a tower is a
 * subset of one**. Passing `inv.tensors` filtered by `towerOf` classifies that
 * stack on its own, which is what keeps its layer indices unambiguous — the
 * same filter-then-fold order `anatomyOf` uses, and for the same reason.
 * `classifyAll` blocks by layer index alone, so classifying a whole multimodal
 * checkpoint puts text layer 3, vision layer 3 and audio layer 3 into one block
 * and lets a sibling-gated rule read across stacks that share nothing.
 *
 * See `sameModelDifferentNames` above for why the key carries rows and not the
 * whole shape: affine packing moves the last axis and leaves the row count
 * alone.
 */
export function cellSignature(tensors: readonly InventoryTensor[]): Set<string> {
  const roles = classifyAll(tensors, undefined, undefined);
  const out = new Set<string>();
  for (const entry of readSide(tensors).values()) {
    const role = roles.get(entry.main.name)?.role;
    const rows = entry.main.shape[0];
    if (role === undefined || rows === undefined) continue;
    const { layer } = layerGroupOf(entry.main.name);
    out.add(`${layer ?? "-"}\u0000${role}\u0000${rows}`);
  }
  return out;
}

/**
 * Shared cells as a fraction of the **smaller** side.
 *
 * Against the smaller side, matching `UNRELATED_OVERLAP` and for the same
 * reason: a stack that is a strict subset of another — a text tower lifted out
 * of a multimodal checkpoint, which also carries a vision stack — is fully
 * accounted for by its parent and should score 1, not `subset ÷ parent`.
 * Dividing by the union would punish exactly the case this measurement exists
 * to recognize.
 *
 * Zero when either side is empty, which is the honest answer: nothing was
 * measured, so nothing lines up.
 */
export function signatureOverlap(
  a: ReadonlySet<string>,
  b: ReadonlySet<string>,
): number {
  const smaller = Math.min(a.size, b.size);
  if (smaller === 0) return 0;
  let shared = 0;
  for (const cell of a) if (b.has(cell)) shared += 1;
  return shared / smaller;
}

function sideTotals(inv: Inventory, side: ReadonlyMap<string, SideTensor>): SideTotals {
  let params = 0;
  for (const tensor of side.values()) params += tensor.params;
  // `inv.totalBytes` rather than a sum over the folded groups: folding never
  // moves a byte, and taking the inventory's own ledger keeps the headline size
  // identical to the one the single-repo view already showed.
  return { tensors: side.size, params, bytes: inv.totalBytes };
}

/**
 * The per-dtype byte ledger of both sides, side by side.
 *
 * Built from each inventory's own `byDtype` tally — raw, unfolded bytes — so the
 * rows add up to the file sizes a visitor can check against the Hub. This is
 * where "the F16 weights became U32 packed words plus a bit of F16 scale plane"
 * becomes visible as an accounting identity rather than an assertion.
 *
 * Ordered by combined weight so the dtypes that moved the most bytes lead;
 * ties break on the dtype name to keep the order total.
 */
function dtypeShift(
  a: Inventory,
  b: Inventory,
): { dtype: string; aBytes: number; bBytes: number }[] {
  const aBytes = new Map(a.byDtype.map((t) => [t.dtype, t.bytes]));
  const bBytes = new Map(b.byDtype.map((t) => [t.dtype, t.bytes]));
  const dtypes = new Set([...aBytes.keys(), ...bBytes.keys()]);

  return [...dtypes]
    .map((dtype) => ({
      dtype,
      aBytes: aBytes.get(dtype) ?? 0,
      bBytes: bBytes.get(dtype) ?? 0,
    }))
    .sort(
      (x, y) =>
        y.aBytes + y.bBytes - (x.aBytes + x.bBytes) ||
        (x.dtype < y.dtype ? -1 : x.dtype > y.dtype ? 1 : 0),
    );
}

interface PairVerdict {
  readonly status: TensorDiffStatus;
  readonly comparable: boolean;
  readonly note: string;
}

/**
 * Decide what happened to one tensor that exists on both sides.
 *
 * The four branches are the four combinations of "is this side an affine
 * triple", and they are kept separate rather than collapsed because the
 * interesting asymmetries live in the mixed ones. Note in particular that a
 * plain tensor facing an *unresolved* triple lands in `shape-changed`: by the
 * time this runs, cross-side anchoring has already tried A's width against every
 * candidate, so an unresolved triple here means no reading of B's shapes
 * reconstructs to A's — which is a shape change, and stating it as one is more
 * useful than reporting an unknown.
 */
function classifyPair(a: SideTensor, b: SideTensor): PairVerdict {
  const aQuant = a.affine !== undefined;
  const bQuant = b.affine !== undefined;
  const sameShape =
    a.shape !== undefined && b.shape !== undefined && sameDims(a.shape, b.shape);

  const shapeChange: PairVerdict = {
    status: "shape-changed",
    comparable: false,
    note: `${extent(a)} → ${extent(b)}, not comparable`,
  };

  if (!aQuant && !bQuant) {
    if (!sameShape) return shapeChange;
    if (a.main.dtype === b.main.dtype) {
      return {
        status: "identical",
        comparable: true,
        note: `unchanged — ${a.main.dtype} ${extent(a)}`,
      };
    }
    return {
      status: "dtype-changed",
      comparable: true,
      note: `${a.main.dtype} → ${b.main.dtype}`,
    };
  }

  if (!aQuant && bQuant) {
    if (!sameShape) return shapeChange;
    return {
      status: "quantized",
      comparable: true,
      note: `${a.main.dtype} → ${scheme(b)}`,
    };
  }

  if (aQuant && !bQuant) {
    if (!sameShape) return shapeChange;
    return {
      status: "dequantized",
      comparable: true,
      note: `${scheme(a)} → ${b.main.dtype}`,
    };
  }

  // Both sides are affine triples: a requantization, or the same quantization
  // twice. Storage equality is checked first and directly, because it is the one
  // reading that holds even when neither scheme is pinned down — two triples
  // with identical part shapes and dtypes are the same tensor whatever the bits
  // turn out to be, and any comparison that can run on A can run on B.
  if (sameStorage(a, b)) {
    return {
      status: "identical",
      comparable: true,
      note: `unchanged — ${scheme(a)}`,
    };
  }
  if (a.shape !== undefined && b.shape !== undefined) {
    if (!sameShape) return shapeChange;
    return {
      status: "dtype-changed",
      comparable: true,
      note: `${scheme(a)} → ${scheme(b)}`,
    };
  }

  // Two different triples, at least one of which nothing resolved — neither the
  // shapes nor the other repo pinned it down, since both sides are packed and
  // neither could anchor the other. The scale planes still line up or they do
  // not, which is the most that can honestly be said; either way `comparable` is
  // false, because dequantizing needs a (bits, group size) we do not have.
  const planesAgree =
    a.affine !== undefined &&
    b.affine !== undefined &&
    a.affine.rows === b.affine.rows &&
    a.affine.scaleCols === b.affine.scaleCols;
  return planesAgree
    ? {
        status: "dtype-changed",
        comparable: false,
        note: `${scheme(a)} → ${scheme(b)}; neither scheme is pinned down, so the reconstruction cannot be checked`,
      }
    : shapeChange;
}

/** Do two triples store byte-for-byte the same parts, in the same dtypes? */
function sameStorage(a: SideTensor, b: SideTensor): boolean {
  const signature = (t: SideTensor): string =>
    t.parts
      .map((p) => `${p.name}|${p.dtype}|${p.shape.join(",")}`)
      .sort()
      .join("\n");
  return signature(a) === signature(b);
}

function sameDims(x: readonly number[], y: readonly number[]): boolean {
  return x.length === y.length && x.every((d, i) => d === y[i]);
}

/** `4096 × 4096`, or `4096 × 512 packed` when the logical width is unknown. */
function extent(t: SideTensor): string {
  const shape = t.shape;
  if (shape === undefined) return `${dims(t.main.shape)} packed`;
  return dims(shape);
}

function dims(shape: readonly number[]): string {
  return shape.length === 0 ? "scalar" : shape.join(" × ");
}

/**
 * `4-bit affine, group 64` when the scheme is known; otherwise the full list of
 * readings the shapes admit, phrased the way `mlx.ts` phrases it. Naming all
 * three is the honest output: committing to one would put a bits-per-weight on
 * screen that is wrong by exactly the factor the guess was wrong by.
 */
function scheme(t: SideTensor): string {
  const resolved = t.scheme;
  if (resolved !== undefined) {
    return `${resolved.bits}-bit affine, group ${resolved.groupSize}`;
  }
  const candidates = t.affine?.candidates ?? [];
  if (candidates.length === 0) return "affine quantized";
  const readings = candidates
    .map((c) => `${c.bits}-bit/group ${c.groupSize}`)
    .join(" or ");
  return `affine quantized, scheme unpinned (${readings})`;
}

/** How a tensor reads on its own, for the rows that exist on one side only. */
function describe(t: SideTensor): string {
  return t.affine === undefined
    ? `${t.main.dtype} ${extent(t)}`
    : `${scheme(t)}, ${extent(t)}`;
}

interface VerdictInput {
  readonly total: number;
  readonly identical: number;
  readonly dtypeChanged: number;
  readonly quantized: number;
  readonly dequantized: number;
  readonly shapeChanged: number;
  readonly onlyA: number;
  readonly onlyB: number;
  readonly comparable: number;
  readonly a: SideTotals;
  readonly b: SideTotals;
  /** `"4-bit"` per quantized row, empty string where the scheme is unpinned. */
  readonly quantBits: readonly string[];
  /**
   * The two sides name their tensors under different conventions.
   *
   * Which makes a zero name-overlap mean something entirely different from
   * "unrelated models" — see `buildVerdict`.
   */
  readonly namingDiffers: boolean;
  /** `"F32 to BF16"` per dtype-changed row. */
  readonly dtypeMoves: readonly string[];
}

/**
 * One sentence a person can read, naming what B *is* relative to A.
 *
 * The cases are ordered by how much they let the reader stop reading. "These are
 * unrelated" and "B is a 4-bit quantization of A" both end the enquiry; the
 * fallback tally does not, and exists only for the genuinely mixed comparisons
 * where no single sentence is true.
 *
 * The dominance test is `>= 0.8` of the changed rows rather than "all of them",
 * because real quantizations are not uniform: a repo can leave its norms and
 * embeddings alone, or untie a head, and losing the headline over two rows out
 * of three hundred would be the wrong trade. The leftovers are still named, so
 * the sentence never claims more than it found.
 */
function buildVerdict(input: VerdictInput): string {
  const { a, b, identical, total } = input;

  if (a.tensors === 0 && b.tensors === 0) {
    return "Neither inventory has any tensors — there is nothing to compare.";
  }
  if (a.tensors === 0 || b.tensors === 0) {
    const empty = a.tensors === 0 ? "A" : "B";
    return `${empty} has no tensors, so there is nothing to line up against the other side.`;
  }

  const shared = total - input.onlyA - input.onlyB;
  const smaller = Math.min(a.tensors, b.tensors);
  if (shared < UNRELATED_OVERLAP * smaller) {
    /*
     * **Two very different findings share one arithmetic.**
     *
     * Zero name overlap means "unrelated models" only when both sides name
     * tensors the same way. Across formats it means nothing of the kind: GGUF
     * writes `blk.0.attn_q.weight` where safetensors writes
     * `model.layers.0.self_attn.q_proj.weight`, so a quantization and the very
     * model it was made from share not one name.
     *
     * That is the pairing the provenance suggestion recommends, so this was the
     * flagship comparison telling visitors to go and check two repo ids that
     * were correct — with the map directly above it placing both models on one
     * grid, contradicting the sentence below (owner, 2026-08-12).
     *
     * The wording says what is true and no more: it names the cause and does not
     * send anyone to check something that is right.
     *
     * **This branch is now much harder to reach, and that is the rekey working.**
     * It said "matching is still by name" and pointed at a future milestone;
     * that milestone landed. `pairSides` pairs across a rename, so a GGUF
     * quantization and its parent now reach the ordinary verdicts below and this
     * sentence is left for the pairs where even the shapes do not line up.
     *
     * **It speaks only for itself**, and that took three wrong versions to
     * settle. It may not gesture at the layout — an earlier draft said "the map
     * *above*" while this renders in the status line with the map *below* it.
     * And it may not assert what another component *did*: "so the map places
     * both by role" was true for a GGUF pair and false the moment one existed
     * whose towers do not correspond, where the grid falls back to "Only A is
     * mapped" and the two messages contradicted each other on one screen
     * (owner's own model, 2026-08-13).
     *
     * This function knows about **names**. Whether the map placed anything is
     * the anatomy's scope decision, taken elsewhere and invisible from here, so
     * the honest sentence stops at the thing it can see. The map's own notice
     * says what the map did.
     */
    if (input.namingDiffers) {
      return "These two use different tensor-naming conventions, so they can't be matched by name.";
    }
    return `These two do not look related — only ${shared} of ${Math.max(a.tensors, b.tensors)} tensor names are shared, so there is nothing useful to line up. Check the two repo ids.`;
  }

  if (identical === total) {
    return `B shares ${identical} of ${identical} tensors with A at identical shapes and dtypes; all ${identical} are comparable.`;
  }

  const changed = total - identical;
  /*
   * Changed rows *among the shared ones*. `identical` is shared by definition,
   * so this is the population the headline's fraction is about — the one-sided
   * rows are reported separately and must not be folded in.
   */
  const changedShared = shared - identical;

  if (input.quantized > 0 && input.quantized >= 0.8 * changed) {
    const label = bitsLabel(input.quantBits);
    return `B is a ${label} quantization of A — ${sameOrPartial(input.quantized, changedShared, shared)}, ${sizeChange(a.bytes, b.bytes)}.`;
  }

  if (input.dequantized > 0 && input.dequantized >= 0.8 * changed) {
    return `B is a dequantization of A back to full-width floats — ${sameOrPartial(input.dequantized, changedShared, shared)}, ${sizeChange(a.bytes, b.bytes)}.`;
  }

  if (input.dtypeChanged > 0 && input.dtypeChanged >= 0.8 * changed) {
    const move = only(input.dtypeMoves) ?? "one dtype to another";
    return `B is A converted from ${move} — ${sameOrPartial(input.dtypeChanged, changedShared, shared)}, ${sizeChange(a.bytes, b.bytes)}.`;
  }

  const parts = [
    input.dtypeChanged > 0 ? `${input.dtypeChanged} changed dtype` : undefined,
    input.quantized > 0 ? `${input.quantized} newly quantized` : undefined,
    input.dequantized > 0 ? `${input.dequantized} dequantized` : undefined,
    input.shapeChanged > 0 ? `${input.shapeChanged} changed shape` : undefined,
    input.onlyA > 0 ? `${input.onlyA} only in A` : undefined,
    input.onlyB > 0 ? `${input.onlyB} only in B` : undefined,
  ].filter((p) => p !== undefined);

  return `B differs from A in ${changed} of ${total} tensors — ${parts.join(", ")}; ${input.comparable} are comparable.`;
}

/**
 * "same 291 tensors" when one kind of change explains the whole diff, and an
 * explicit fraction when it does not. The second form is what keeps the 0.8
 * dominance test honest — a verdict that says "same 291 tensors" while 12 rows
 * say otherwise would be the tool contradicting its own table.
 */
function sameOrPartial(kind: number, changedShared: number, shared: number): string {
  /*
   * The second argument is the count of changed rows **among the shared ones**,
   * and it used to be the count of changed rows overall.
   *
   * Those differ by the one-sided rows, so subtracting `kind` described tensors
   * that exist on only one side as "differing some other way" — which they are
   * not. They are absent, which is a different finding and the more
   * consequential one, and the sentence already reports it elsewhere.
   *
   * Invisible until the rekey, because this branch is reached only once enough
   * tensors pair to earn a headline. On the owner's own pair it then read "278
   * of 540 shared tensors, 60 differing some other way", where the 540 shared
   * are fully accounted for — 278 dequantized, 262 identical — and the 60 are
   * B's alone.
   */
  const otherwise = Math.max(0, changedShared - kind);
  return otherwise === 0
    ? `same ${shared} tensors`
    : `${kind} of ${shared} shared tensors, ${otherwise} differing some other way`;
}

/**
 * `4-bit affine`, or an honest hedge when the rows do not agree on one width.
 *
 * A `"quantized"` row always carries a resolved scheme — that status is reached
 * only when B's logical shape is known, which is exactly when its (bits, group
 * size) has been pinned — so the empty-string case here is defensive rather than
 * a live path.
 */
function bitsLabel(quantBits: readonly string[]): string {
  const widths = [...new Set(quantBits.filter((w) => w !== ""))].sort();
  const first = widths[0];
  if (first === undefined) return "an affine";
  return widths.length === 1
    ? `${first} affine`
    : `mixed-precision affine (${widths.join(", ")})`;
}

/** The single distinct value, or `undefined` when there is more than one. */
function only(values: readonly string[]): string | undefined {
  const distinct = new Set(values);
  return distinct.size === 1 ? [...distinct][0] : undefined;
}

/**
 * "3.9× smaller" — the number a visitor comparing a quant against its base
 * actually came for.
 *
 * A ratio within half a percent reads as "the same size" rather than "1.0×
 * smaller", which would be a strange thing to say about two equal numbers.
 */
function sizeChange(aBytes: number, bBytes: number): string {
  if (aBytes <= 0 || bBytes <= 0) return "with no byte totals to compare";
  const ratio = aBytes / bBytes;
  if (ratio > 1.005) return `${ratioText(ratio)}× smaller`;
  if (ratio < 0.995) return `${ratioText(1 / ratio)}× larger`;
  return "the same size";
}

function ratioText(ratio: number): string {
  return ratio < 10 ? ratio.toFixed(1) : String(Math.round(ratio));
}
